import { useRef, useState, useEffect } from 'react';
import { useReadOut } from './useReadOut';
import type { VoiceGender } from './voices';

// ---------------------------------------------------------------------------
// Position persistence
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'readout-position';
interface Position {
  right: number;
  bottom: number;
}

function loadPosition(): Position {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return { right: 24, bottom: 24 };
}
function savePosition(pos: Position) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {}
}

// ---------------------------------------------------------------------------
// Color utilities — parse → HSL → derive widget palette
// ---------------------------------------------------------------------------
function parseCssColor(color: string): [number, number, number] | null {
  if (!color) return null;
  const hex = color.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return [
      parseInt(hex[0] + hex[0], 16),
      parseInt(hex[1] + hex[1], 16),
      parseInt(hex[2] + hex[2], 16)
    ];
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16)
    ];
  }
  const m = color.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
  if (m) return [+m[1], +m[2], +m[3]];
  // Named colors — resolve via a temporary DOM element
  try {
    const div = document.createElement('div');
    div.style.backgroundColor = color;
    document.body.appendChild(div);
    const computed = window.getComputedStyle(div).backgroundColor;
    document.body.removeChild(div);
    const cm = computed.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
    if (cm) return [+cm[1], +cm[2], +cm[3]];
  } catch {}
  return null;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255,
    gn = g / 255,
    bn = b / 255;
  const max = Math.max(rn, gn, bn),
    min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
        break;
      case gn:
        h = ((bn - rn) / d + 2) / 6;
        break;
      case bn:
        h = ((rn - gn) / d + 4) / 6;
        break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  const hn = h / 360,
    sn = s / 100,
    ln = l / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    const tt = ((t % 1) + 1) % 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let r: number, g: number, b: number;
  if (sn === 0) {
    r = g = b = ln;
  } else {
    const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
    const p = 2 * ln - q;
    r = hue2rgb(p, q, hn + 1 / 3);
    g = hue2rgb(p, q, hn);
    b = hue2rgb(p, q, hn - 1 / 3);
  }
  const toHex = (c: number) =>
    Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Build a complete widget palette from the page background color.
// Light page → dark widget (same hue, very low L).
// Dark page  → light widget (same hue, very high L).
function deriveTheme(pageBg: string) {
  const rgb = parseCssColor(pageBg);
  const [h, s, l] = rgb ? rgbToHsl(...rgb) : [0, 0, 100];
  const isLight = l > 50;

  // Reduce saturation so very vivid hues don't overpower the widget
  const ws = Math.min(s * 0.5, 30);

  if (isLight) {
    // Dark widget on light page
    const pillBg = hslToHex(h, ws, Math.max(l - 88, 8));
    const pillBorder = hslToHex(h, ws, Math.max(l - 78, 16));
    const divider = hslToHex(h, ws * 0.6, Math.max(l - 70, 22));
    const speakerBg = hslToHex(h, ws, Math.max(l - 82, 12));
    const settingsBg = hslToHex(h, ws, Math.max(l - 90, 5));
    const settingsBrd = hslToHex(h, ws * 0.6, Math.max(l - 75, 18));
    return {
      isLight,
      pill: {
        bg: pillBg,
        border: `1.5px solid ${pillBorder}`,
        shadow: '0 4px 24px rgba(0,0,0,0.3)'
      },
      divider,
      dragColor: 'rgba(255,255,255,0.4)',
      speakerBg,
      speakerClr: 'rgba(255,255,255,0.55)',
      settingsClr: 'rgba(255,255,255,0.5)',
      settingsActiveBg: speakerBg,
      settingsActiveClr: '#a5b4fc',
      panel: {
        bg: settingsBg,
        border: `1px solid ${settingsBrd}`,
        text: 'rgba(255,255,255,0.92)',
        subtext: 'rgba(255,255,255,0.55)',
        muted: 'rgba(255,255,255,0.35)',
        closeClr: 'rgba(255,255,255,0.4)',
        previewBg: pillBorder,
        previewBrd: `1px solid ${divider}`,
        previewClr: 'rgba(255,255,255,0.9)',
        resetClr: 'rgba(255,255,255,0.5)',
        resetBrd: `1px solid ${divider}`,
        accent: '#818cf8'
      }
    };
  } else {
    // Light widget on dark page
    const pillBg = hslToHex(h, ws * 0.5, Math.min(l + 78, 97));
    const pillBorder = hslToHex(h, ws * 0.6, Math.min(l + 60, 90));
    const divider = hslToHex(h, ws * 0.4, Math.min(l + 52, 86));
    const speakerBg = hslToHex(h, ws * 0.4, Math.min(l + 68, 94));
    const settingsBg = hslToHex(h, ws * 0.5, Math.min(l + 80, 98));
    const settingsBrd = hslToHex(h, ws * 0.5, Math.min(l + 62, 91));
    return {
      isLight,
      pill: {
        bg: pillBg,
        border: `1.5px solid ${pillBorder}`,
        shadow: '0 4px 20px rgba(0,0,0,0.15)'
      },
      divider,
      dragColor: hslToHex(h, ws * 0.6, Math.min(l + 25, 65)),
      speakerBg,
      speakerClr: hslToHex(h, ws * 0.8, Math.max(l - 15, 30)),
      settingsClr: hslToHex(h, ws * 0.8, Math.max(l - 15, 30)),
      settingsActiveBg: speakerBg,
      settingsActiveClr: '#4f46e5',
      panel: {
        bg: settingsBg,
        border: `1px solid ${settingsBrd}`,
        text: hslToHex(h, ws * 0.5, Math.max(l - 60, 8)),
        subtext: hslToHex(h, ws * 0.4, Math.max(l - 35, 35)),
        muted: hslToHex(h, ws * 0.3, Math.max(l - 20, 50)),
        closeClr: hslToHex(h, ws * 0.3, Math.max(l - 20, 55)),
        previewBg: speakerBg,
        previewBrd: `1px solid ${divider}`,
        previewClr: hslToHex(h, ws * 0.5, Math.max(l - 55, 10)),
        resetClr: hslToHex(h, ws * 0.4, Math.max(l - 30, 40)),
        resetBrd: `1px solid ${divider}`,
        accent: '#4f46e5'
      }
    };
  }
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
const SpeakerOffIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);
const SpeakerOnIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);
const SettingsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const DragIcon = ({ color }: { color: string }) => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill={color}>
    <circle cx="2.5" cy="3" r="1.4" />
    <circle cx="7.5" cy="3" r="1.4" />
    <circle cx="2.5" cy="8" r="1.4" />
    <circle cx="7.5" cy="8" r="1.4" />
    <circle cx="2.5" cy="13" r="1.4" />
    <circle cx="7.5" cy="13" r="1.4" />
  </svg>
);

const PREVIEW_TEXT: Record<string, string> = {
  'en-US': 'The quick brown fox jumps over the lazy dog.',
  'en-GB': 'The quick brown fox jumps over the lazy dog.',
  'en-AU': 'The quick brown fox jumps over the lazy dog.'
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ReadOutButtonProps {
  lang: string;
  gender: VoiceGender;
  pageBg: string;
}

export function ReadOutButton({ lang, gender, pageBg }: ReadOutButtonProps) {
  const [position, setPosition] = useState<Position>(loadPosition);
  const [showSettings, setShowSettings] = useState(false);
  const [rate, setRate] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
  } | null>(null);

  const { active, toggle, preview } = useReadOut(
    lang,
    gender,
    rate,
    pitch,
    containerRef as React.RefObject<HTMLElement | null>,
    showSettings
  );

  useEffect(() => {
    if (!active) return;
    document.body.style.cursor = 'crosshair';
    return () => {
      document.body.style.cursor = '';
    };
  }, [active]);

  const handleDragStart = (event: React.PointerEvent) => {
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      startRight: position.right,
      startBottom: position.bottom
    };
    const handleMove = (e: PointerEvent) => {
      if (!dragState.current) return;
      setPosition({
        right: Math.max(8, dragState.current.startRight - (e.clientX - dragState.current.startX)),
        bottom: Math.max(8, dragState.current.startBottom - (e.clientY - dragState.current.startY))
      });
    };
    const handleUp = () => {
      if (dragState.current)
        setPosition((prev) => {
          savePosition(prev);
          return prev;
        });
      dragState.current = null;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  const theme = deriveTheme(pageBg);
  const p = theme.panel;

  const langLabel =
    lang === 'en-GB' ? 'English (UK)' : lang === 'en-AU' ? 'English (AU)' : 'English (US)';
  const genderLabel =
    gender === 'female' ? 'Female voice' : gender === 'male' ? 'Male voice' : 'Auto voice';

  return (
    <>
      <style>{`
        @keyframes readout-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.3; transform: scale(0.65); }
        }
      `}</style>

      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          right: position.right,
          bottom: position.bottom,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 10,
          userSelect: 'none'
        }}
      >
        {/* Settings panel */}
        {showSettings && (
          <div
            style={{
              background: p.bg,
              border: p.border,
              borderRadius: 16,
              padding: '16px 18px',
              width: 260,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              color: p.text
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: 14
              }}
            >
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: p.text }}>
                  Readout settings
                </p>
                <p style={{ fontSize: 12, color: p.subtext, margin: '3px 0 0' }}>
                  {langLabel} · {genderLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: p.closeClr,
                  cursor: 'pointer',
                  padding: 2,
                  lineHeight: 1,
                  fontSize: 14
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: p.text }}>Speed</span>
                  <span style={{ fontSize: 13, color: p.subtext }}>{rate.toFixed(1)}×</span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={2}
                  step={0.1}
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: p.accent }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  <span style={{ fontSize: 11, color: p.muted }}>Slower</span>
                  <span style={{ fontSize: 11, color: p.muted }}>Faster</span>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: p.text }}>Pitch</span>
                  <span style={{ fontSize: 13, color: p.subtext }}>{pitch.toFixed(1)}</span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={2}
                  step={0.1}
                  value={pitch}
                  onChange={(e) => setPitch(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: p.accent }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  <span style={{ fontSize: 11, color: p.muted }}>Lower</span>
                  <span style={{ fontSize: 11, color: p.muted }}>Higher</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => preview(PREVIEW_TEXT[lang] ?? PREVIEW_TEXT['en-US'])}
                  style={{
                    flex: 1,
                    padding: '7px 0',
                    borderRadius: 8,
                    background: p.previewBg,
                    border: p.previewBrd,
                    color: p.previewClr,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5
                  }}
                >
                  <span style={{ fontSize: 11 }}>▶</span> Preview
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRate(1.0);
                    setPitch(1.0);
                  }}
                  style={{
                    flex: 1,
                    padding: '7px 0',
                    borderRadius: 8,
                    background: 'none',
                    border: p.resetBrd,
                    color: p.resetClr,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active hint */}
        {active && !showSettings && (
          <div
            style={{
              background: 'rgba(0,0,0,0.78)',
              backdropFilter: 'blur(6px)',
              borderRadius: 999,
              padding: '6px 14px',
              fontSize: 13,
              color: '#e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              pointerEvents: 'none'
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#3b82f6',
                display: 'inline-block',
                flexShrink: 0,
                animation: 'readout-pulse 1.4s ease-in-out infinite'
              }}
            />
            Hover any text to listen
          </div>
        )}

        {/* Pill */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: theme.pill.bg,
            border: theme.pill.border,
            borderRadius: 999,
            boxShadow: theme.pill.shadow,
            height: 60,
            overflow: 'hidden'
          }}
        >
          <span
            onPointerDown={handleDragStart}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: '100%',
              cursor: 'grab',
              flexShrink: 0
            }}
          >
            <DragIcon color={theme.dragColor} />
          </span>

          <span style={{ width: 1.5, height: 28, background: theme.divider, flexShrink: 0 }} />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 8px'
            }}
          >
            <button
              type="button"
              onClick={toggle}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                border: 'none',
                cursor: 'pointer',
                borderRadius: '50%',
                flexShrink: 0,
                background: active ? '#3b82f6' : theme.speakerBg,
                color: active ? '#ffffff' : theme.speakerClr,
                transition: 'background 150ms, color 150ms'
              }}
              title={active ? 'Deactivate readout' : 'Activate readout'}
            >
              {active ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
            </button>
          </div>

          <span style={{ width: 1.5, height: 28, background: theme.divider, flexShrink: 0 }} />

          <button
            type="button"
            onClick={() => setShowSettings((prev) => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: '100%',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              background: showSettings ? theme.settingsActiveBg : 'transparent',
              color: showSettings ? theme.settingsActiveClr : theme.settingsClr,
              transition: 'background 150ms, color 150ms'
            }}
            title="Readout settings"
          >
            <SettingsIcon />
          </button>
        </div>
      </div>
    </>
  );
}
